import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Grid2 as Grid,
  Container,
  TextField,
  InputAdornment,
  IconButton,
  useMediaQuery,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { DataGrid } from "@mui/x-data-grid";

import { useSelector, useDispatch } from "react-redux";
import { useEffect, useRef, useState } from "react";
import {
  clearPermitPackages,
  getCustomers,
  getPermitPackages,
} from "../../store/actions/dataActions";
import CustomerInfoCard from "./management/CustomerInfoCard";
import { setActiveTab } from "../../store/actions/uiActions/myPermitsActions";
import CustomerTabs from "../pages/admin/CustomerTabs";

const CustomersGrid = ({
  customerSelectedRow,
  setCustomerSelectedRow,
  dataLoading,
  setDataLoading,
}) => {
  const dispatch = useDispatch();
  const customers = useSelector((state) => state.nocData?.customers);
  const { activeTab } = useSelector((state) => state.myPermitsComponent);

  const mobileSize = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const tabletSize = useMediaQuery((theme) => theme.breakpoints.down("md"));

  const [packagePage, setPackagePage] = useState(0);
  const [packagePageSize, setpackagePageSize] = useState(5);
  const [packageRowCount, setPackageRowCount] = useState(0);
  const [packageLoading, setPackageLoading] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [searchValue, setSearchValue] = useState(
    customers?.pagination?.searchTerm || "",
  );
  const [customerFilter, setCustomerFilter] = useState({ type: "all" });

  const customerInfoCardRef = useRef(false);

  const didInit = useRef(false);
  const searchTimeoutRef = useRef(null);
  const didFilterInit = useRef(false);
  const pagination = customers?.pagination || {};
  const currentPage =
    typeof pagination?.page === "number" ? pagination.page : 0;
  const currentPageSize =
    typeof pagination?.rowsPerPage === "number" ? pagination.rowsPerPage : 5;

  useEffect(() => {
    if (!didInit.current) {
      didInit.current = true;
      if (activeTab !== 0) {
        dispatch(setActiveTab(0));
      }
    }
  }, [activeTab, dispatch]);

  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // When the tab filter changes, refetch from page 0 – but skip the very first run
  // so we don't clobber the initial customers/selection restored by Customers.js
  useEffect(() => {
    if (!didFilterInit.current) {
      didFilterInit.current = true;
      return;
    }
    fetchCustomers({
      page: 0,
      pageSize: pagination?.rowsPerPage || currentPageSize,
      search: searchValue.trim(),
      filter: customerFilter,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerFilter]);

  const fetchCustomers = async ({
    page = currentPage,
    pageSize = currentPageSize,
    search = searchValue.trim(),
    filter = customerFilter,
  } = {}) => {
    setDataLoading(true);
    await dispatch(clearPermitPackages());
    setSelectedPackage(null);

    try {
      return await dispatch(
        getCustomers({
          page,
          rowsPerPage: pageSize,
          searchTerm: search,
          filterType: filter?.type || null,
          fromDate: filter?.fromDate || null,
          toDate: filter?.toDate || null,
        }),
      );
    } finally {
      setDataLoading(false);
    }
  };

  // If the currently selected customer is no longer in the loaded rows
  // (e.g. user changed page/filter/search), clear the selection.
  useEffect(() => {
    if (!customerSelectedRow) return;
    const rows = customers?.rows || [];
    const exists = rows.some((row) => row.id === customerSelectedRow.id);
    if (!exists) {
      setCustomerSelectedRow(null);
      setSelectedPackage(null);
    }
  }, [
    customers?.rows,
    customerSelectedRow,
    setCustomerSelectedRow,
    setSelectedPackage,
  ]);

  const handleRowClick = (params, event, details) => {
    if (customerSelectedRow?.id !== params?.row?.id) {
      setPackageLoading(true);
      setSelectedPackage(null);
      setCustomerSelectedRow(params?.row); // updates state and UI first

      // fire and forget: let this run in background
      (async () => {
        const result = await dispatch(
          getPermitPackages({
            newPage: 0,
            newPageSize: 5,
            customerData: {
              email: params?.row?.email,
              role: params?.row?.role,
            },
          }),
        );
        setPackageRowCount(result?.data || 0);
        setpackagePageSize(5);
        setPackagePage(0);
        setPackageLoading(false);
      })();
    }
    setTimeout(
      () =>
        customerInfoCardRef.current?.scrollIntoView({
          behavior: "smooth",
        }),
      50,
    );
  };

  const handlePaginationModelChange = (model) => {
    if (dataLoading) {
      return;
    }

    const requestedPageSize = model.pageSize;
    const isPageSizeChange = requestedPageSize !== pagination?.rowsPerPage;
    const nextPage = isPageSizeChange ? 0 : model.page;

    fetchCustomers({
      page: nextPage,
      pageSize: requestedPageSize,
      search: searchValue.trim(),
      filter: customerFilter,
    });
  };

  const triggerSearch = (value, filter = "all") => {
    const nextFilter = { type: filter };
    const hasFilterChanged =
      customerFilter?.type !== nextFilter?.type ||
      customerFilter?.fromDate !== nextFilter?.fromDate ||
      customerFilter?.toDate !== nextFilter?.toDate;

    if (hasFilterChanged) {
      setCustomerFilter(nextFilter);
      return;
    }

    const sanitizedValue = value.trim();
    fetchCustomers({
      page: 0,
      pageSize: pagination?.rowsPerPage || 5,
      search: sanitizedValue,
      filter: nextFilter,
    });
  };

  const handleSearchChange = (event) => {
    const value = event.target.value;
    setSearchValue(value);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      triggerSearch(value.trim());
    }, 500);
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    triggerSearch(searchValue.trim());
  };

  const handleClearSearch = () => {
    if (!searchValue) {
      return;
    }
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setSearchValue("");
    triggerSearch("");
  };

  const customersColumnSmall = [
    {
      field: "customerName",
      headerName: "Customers",
      flex: 1,
      editable: false,
      renderCell: (params) => {
        return (
          <Box
            sx={{
              width: "100%",
              py: 0.6,

              display: "flex",
              flexDirection: "column",
              gap: 0.45,
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "0.95rem",
                    fontWeight: 700,
                    lineHeight: 1.2,
                    wordBreak: "break-word",
                  }}
                >
                  {params?.value || "N/A"}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "0.72rem",
                    color: "rgba(0,0,0,0.62)",
                    flexShrink: 0,
                    lineHeight: 1.2,
                    whiteSpace: "nowrap",
                  }}
                >
                  {params?.row?.registrationDate || "N/A"}
                </Typography>
              </Box>
              <Typography
                sx={{
                  fontSize: "0.78rem",
                  color: "rgba(0,0,0,0.62)",
                  lineHeight: 1.2,
                  mt: 0.25,
                  wordBreak: "break-word",
                }}
              >
                {params?.row?.role || "N/A"} • {params?.row?.tierName || "N/A"}
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.74rem",
                  color: "rgba(0,0,0,0.62)",
                  lineHeight: 1.2,
                  mt: 0.15,
                }}
              >
                {params?.row?.email || "N/A"}
              </Typography>
            </Box>

            <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 1.0 }}>
              <Box
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.4 }}
              >
                <Typography
                  sx={{ fontSize: "0.72rem", color: "rgba(0,0,0,0.62)" }}
                >
                  Coupons
                </Typography>
                {params?.row?.has_active_coupons === null ? (
                  <Typography
                    sx={{ fontSize: "0.72rem", color: "rgba(0,0,0,0.62)" }}
                  >
                    N/A
                  </Typography>
                ) : params?.row?.has_active_coupons ? (
                  <CheckCircleIcon sx={{ color: "green", fontSize: "1rem" }} />
                ) : (
                  <CancelIcon sx={{ color: "red", fontSize: "1rem" }} />
                )}
              </Box>
              <Box
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.4 }}
              >
                <Typography
                  sx={{ fontSize: "0.72rem", color: "rgba(0,0,0,0.62)" }}
                >
                  Active
                </Typography>
                {params?.row?.status === null ? (
                  <Typography
                    sx={{ fontSize: "0.72rem", color: "rgba(0,0,0,0.62)" }}
                  >
                    N/A
                  </Typography>
                ) : params?.row?.status ? (
                  <CheckCircleIcon sx={{ color: "green", fontSize: "1rem" }} />
                ) : (
                  <CancelIcon sx={{ color: "red", fontSize: "1rem" }} />
                )}
              </Box>
            </Box>
          </Box>
        );
      },
    },
  ];
  const customersColumn = [
    {
      field: "registrationDate",
      headerName: "Registration Date",
      flex: 1.2,
      editable: false,
    },
    {
      field: "customerName",
      headerName: "Customer Name",
      flex: 1.5,
      editable: false,
    },
    {
      field: "role",
      headerName: "Role",
      flex: 1,
      editable: false,
    },
    {
      field: "email",
      headerName: "Email",
      flex: 2,
      editable: false,
    },
    {
      field: "tierName",
      headerName: "Base Plan",
      flex: 1,
      editable: false,
    },
    {
      field: "has_active_coupons",
      headerName: "Coupons",
      headerAlign: "center",
      flex: 0.8,
      editable: false,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
          }}
        >
          {params.value ? (
            <CheckCircleIcon style={{ color: "green" }} />
          ) : (
            <CancelIcon style={{ color: "red" }} />
          )}
        </Box>
      ),
    },
    {
      field: "status",
      headerName: "Active",
      headerAlign: "center",
      flex: 0.65,
      editable: false,
      renderCell: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            height: "100%",
            width: "100%",
          }}
        >
          {params.value ? (
            <CheckCircleIcon style={{ color: "green" }} />
          ) : (
            <CancelIcon style={{ color: "red" }} />
          )}
        </Box>
      ),
    },
  ];

  return (
    <>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: tabletSize ? 0 : 1,
          backgroundColor: "#EEF2F6",
        }}
      >
        <Container
          maxWidth="xl"
          sx={{ px: tabletSize ? "0px !important" : "24px !important" }}
        >
          <Grid container sx={{ my: tabletSize ? 0 : 2 }} spacing={3}>
            <Grid size={{ xs: 12 }}>
              <Card sx={{ borderRadius: mobileSize ? 0 : 2.5 }}>
                <CardHeader
                  title="Customers"
                  sx={{
                    flexDirection: mobileSize ? "column" : "row",
                    gap: mobileSize ? 1.5 : 0,
                    px: mobileSize ? 1.5 : 3,
                    pt: mobileSize ? 3 : 4,
                  }}
                  slotProps={{
                    action: { sx: { width: mobileSize ? "100%" : "auto" } },
                  }}
                  action={
                    <Box
                      component="form"
                      onSubmit={handleSearchSubmit}
                      sx={{
                        minWidth: { xs: "100%", sm: 260 },
                      }}
                    >
                      <TextField
                        fullWidth
                        size="small"
                        type="search"
                        label="Search customers"
                        variant="filled"
                        value={searchValue}
                        onChange={handleSearchChange}
                        slotProps={{
                          htmlInput: {
                            sx: {
                              "&::-webkit-search-cancel-button": {
                                display: "none",
                              },
                            },
                          },
                          input: {
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                              </InputAdornment>
                            ),
                            endAdornment: searchValue ? (
                              <InputAdornment position="end">
                                <IconButton
                                  size="small"
                                  onClick={handleClearSearch}
                                  aria-label="Clear search"
                                  sx={{
                                    p: 0.75,
                                    cursor: "pointer",
                                  }}
                                >
                                  <ClearIcon fontSize="small" />
                                </IconButton>
                              </InputAdornment>
                            ) : null,
                          },
                        }}
                      />
                    </Box>
                  }
                />
                <CardContent
                  sx={{
                    px: mobileSize ? 1.5 : 3,
                    pt: mobileSize ? 2 : 4,
                  }}
                >
                  <CustomerTabs onFilterChange={setCustomerFilter} />
                  <DataGrid
                    sx={{
                      "&.MuiDataGrid-root .MuiDataGrid-cell:focus-within": {
                        outline: "none !important",
                      },
                      "& .MuiDataGrid-columnHeader:focus-within": {
                        outline: "none !important",
                      },
                      "& .MuiDataGrid-columnHeader": {
                        ".MuiDataGrid-menuIcon": {
                          display: "none",
                          pointerEvents: "none",
                        },
                        "&:hover": {
                          ".MuiDataGrid-menuIcon, .MuiDataGrid-sortIcon, .MuiDataGrid-columnHeader--sorted":
                            {
                              display: "none",
                              pointerEvents: "none",
                            },
                          cursor: "default",
                        },
                        backgroundColor: "#333333", // Change column header background color
                        color: "white", // Optional: Change text color for contrast
                      },
                      // Additionally target specific sort button
                      "& .MuiDataGrid-columnHeader--sortable": {
                        pointerEvents: "none",
                      },
                      "& .MuiDataGrid-columnHeaders": {
                        padding: 0, // Adjust as needed
                        margin: 0, // Adjust as needed
                      },
                      "& .MuiDataGrid-columnHeadersInner": {
                        width: "100%", // Adjust as needed
                        padding: 0,
                      },
                      border: "1px solid #FCF2F0", // Change the border color of the entire DataGrid
                      "& .MuiDataGrid-cell": {
                        borderColor: "#333333", // Change cell border color
                        px: tabletSize ? "6px" : undefined,
                      },
                      "& .MuiDataGrid-columnSeparator": {
                        display: "none", // Optional: Hide the column separators
                      },
                      "& .MuiDataGrid-row": {
                        cursor: "pointer", // Change cursor to pointer for rows
                        // backgroundColor: "#fff4c0 !important",
                      },
                      // Apply light yellow background for specified rows
                      "& .light-yellow-row": {
                        backgroundColor: "#fff4c0",
                      },
                      "& .selected-row-color": {
                        backgroundColor: "#ebf1f4 !important",
                      },
                      // Apply darker yellow only for selected rows that are light-yellow
                      "& .light-yellow-row.Mui-selected": {
                        backgroundColor: "#ffe499 !important", // Slightly darker yellow
                      },
                    }}
                    rows={customers?.rows || []}
                    columns={
                      tabletSize ? customersColumnSmall : customersColumn
                    }
                    getRowId={(row) => row?.id}
                    onPaginationModelChange={(model, details) => {
                      handlePaginationModelChange(model, details);
                    }}
                    loading={dataLoading}
                    paginationMode="server"
                    paginationModel={{
                      page: customers?.pagination?.page, // Set the current page value here
                      pageSize: customers?.pagination?.rowsPerPage, // Set the page size value here
                    }}
                    rowCount={customers?.pagination?.totalCount}
                    initialState={{
                      pagination: {
                        paginationModel: {
                          pageSize: 5,
                        },
                      },
                    }}
                    onRowClick={(params, event, details) => {
                      handleRowClick(params, event, details);
                    }}
                    pageSizeOptions={[5, 25, 50, 100]}
                    rowHeight={tabletSize ? 82 : 34}
                  />

                  {customerSelectedRow && (
                    <CustomerInfoCard
                      customerSelectedRow={customerSelectedRow}
                      setCustomerSelectedRow={setCustomerSelectedRow}
                      packagePage={packagePage}
                      setPackagePage={setPackagePage}
                      packagePageSize={packagePageSize}
                      setpackagePageSize={setpackagePageSize}
                      packageLoading={packageLoading}
                      setPackageLoading={setPackageLoading}
                      packageRowCount={packageRowCount}
                      setPackageRowCount={setPackageRowCount}
                      selectedPackage={selectedPackage}
                      setSelectedPackage={setSelectedPackage}
                      customerInfoCardRef={customerInfoCardRef}
                    />
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default CustomersGrid;
